/**
 * PatientEditPage — the 6-tab shell around the create / edit patient
 * experience. Owns:
 *
 *   - Top bar (back, photo, name + Merged badge, breadcrumb, Save/Next)
 *   - Tab strip (6 tabs, permission-gated + visibility-gated)
 *   - Tab body slot (each tab body owns its form; this shell renders the
 *     active one via `PatientEditShellProvider`)
 *   - URL state for the active tab (`?tab=<key>`) so back/forward works
 *   - Cancel-confirm when the active tab reports `isDirty`
 *
 * Tab bodies (`PersonalDetailsTab`, `ContactDetailsTab`, ...) ship as
 * placeholder stubs in this PR and get replaced by PROD-725..738. They
 * each consume `usePatientEditShell()` to register their save action and
 * dirty state.
 *
 * Routes (from `patients-routes.tsx`):
 *   - `/patients/new`                  → create-mode, no patient loaded
 *   - `/patients/edit/:patientId`      → edit-mode, `usePatient` loads the patient
 *
 * Flutter reference: `create_patient_screen.dart:606` (`body()`) and
 * `:1114` (`selectedTabWidget()`). Permission gates match
 * `:840` (`Patient_Contacts`), `:909` (`Access_Patient_Billing`),
 * `:865` (`!isMerged && patientId != null`).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ChevronLeft, Info, User } from 'lucide-react';
import { Avatar, Button, PageScaffold, SubTabs, type SubTabDef } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { PageHeader } from '@/shared/ui/components/page-header';
import { MessageColleagueAction } from '@/staff-chat/components/message-colleague-action';
import { isStaffChatPatientOn } from '@/staff-chat/lib/staff-chat-switch';
import { FormActionBar, type FormSaveStatus } from '@/shared/ui/components/form-action-bar';
import { usePermission } from '@/auth/permission-gate';
import { usePatient } from '@/patients/services/use-patient';
import {
  PatientEditShellProvider,
  usePatientEditShell,
} from '@/patients/components/patient-edit-shell-context';
import { CancelChangesDialog } from '@/patients/components/cancel-changes-dialog';
import { useNavGuard } from '@/shared/store/nav-guard';
import { PersonalDetailsTab } from '@/patients/tabs/personal-details-tab';
import { ContactDetailsTab } from '@/patients/tabs/contact-details-tab';
import { ChartTab } from '@/patients/tabs/chart-tab';
import { CommunityTab } from '@/patients/tabs/community-tab';
import { BillingTab } from '@/patients/tabs/billing-tab';
import { AppointmentsTab } from '@/patients/tabs/appointments-tab';
import { PatientCarePlanTab } from '@/patients/components/care-plan/patient-care-plan-tab';
import { PatientOverviewTab, type OverviewTarget } from '@/patients/components/overview/patient-overview-tab';
import { usePatientPages } from '@/patients/lib/patient-pages-flag';
import { formatDateD45 } from '@/shared/lib/date-format';

import {
  CHART_INNER_PARAM,
  CHART_TAB_PARAM,
  PATIENT_EDIT_TAB_PARAM,
  type PatientEditTabKey as TabKey,
} from '@/patients/lib/patient-edit-tabs';

interface TabDescriptor {
  key: TabKey;
  label: string;
  /** True when the tab should render at all on the current mode. */
  visible: boolean;
  /** True when the user is allowed to interact with this tab. */
  enabled: boolean;
}

export function PatientEditPage() {
  return (
    <PatientEditShellProvider>
      <PatientEditPageInner />
    </PatientEditShellProvider>
  );
}

function PatientEditPageInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams<{ patientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCreateMode = !patientId;
  // Patient pages switch (`?x=patient-pages`): an Overview hub as the first tab and the landing tab of an
  // existing patient. OFF = the six tabs, landing on Personal Details, exactly as before.
  const patientPages = usePatientPages();

  // Origin-aware Back (Flutter `doPop` = Navigator.pop → returns to whatever
  // pushed this screen — e.g. the claim-review popup re-opens on the workbench,
  // review_claim_popup.dart:688-707). Callers pass `state.from`; fallback is
  // the patients list. Captured ONCE on mount: the tab strip drives
  // `?tab=` via setSearchParams, which replaces `location.state` with null, so
  // reading it live would lose `from` after the first tab switch.
  const [backTo] = useState<string | null>(
    () => (location.state as { from?: string } | null)?.from ?? null,
  );

  const { data: patient, isLoading } = usePatient(patientId);
  const isMerged = patient?.isMerged === true;

  const canViewContacts = usePermission(['Patient_Contacts'], { view: true });
  const canViewBilling = usePermission(['Access_Patient_Billing'], { view: true });

  // Edit-mode tabs only unlock once we have a patient loaded (Flutter
  // disables tab buttons via `onPressed: null` during the initial fetch).
  const hasPatient = !!patient?.patientId;
  const subTabsAvailable = hasPatient && !isMerged;

  const overviewLands = patientPages && !isCreateMode && !isMerged;
  const defaultTab: TabKey = overviewLands ? 'overview' : 'personal-details';

  const tabs = useMemo<TabDescriptor[]>(() => {
    return [
      ...(overviewLands
        ? [{ key: 'overview' as const, label: 'Overview', visible: true, enabled: hasPatient }]
        : []),
      {
        key: 'personal-details',
        label: 'Personal details',
        visible: true,
        enabled: true,
      },
      {
        key: 'contact-details',
        label: 'Contact details',
        visible: canViewContacts,
        enabled: canViewContacts && hasPatient,
      },
      {
        key: 'chart',
        label: 'Chart',
        visible: subTabsAvailable,
        enabled: subTabsAvailable,
      },
      {
        key: 'community',
        label: 'Community',
        visible: subTabsAvailable && canViewContacts,
        enabled: subTabsAvailable && canViewContacts,
      },
      {
        key: 'billing',
        label: 'Billing',
        visible: subTabsAvailable && canViewBilling,
        enabled: subTabsAvailable && canViewBilling,
      },
      {
        key: 'appointments',
        label: 'Appointments',
        visible: subTabsAvailable,
        enabled: subTabsAvailable,
      },
      // The design's record tab order: Overview, Personal details, Contact details, Chart, Community, Billing,
      // Appointments, Care plan (design-export demo/patient-workspace.ts `WORKSPACE_TABS`). Still behind the switch.
      ...(overviewLands
        ? [
            {
              key: 'care-plan' as const,
              label: 'Care plan',
              visible: subTabsAvailable,
              enabled: subTabsAvailable,
            },
          ]
        : []),
    ];
  }, [canViewContacts, canViewBilling, hasPatient, subTabsAvailable, overviewLands]);

  // URL-driven tab state. Default to personal-details. If the URL points at
  // a tab that's not visible (e.g. ?tab=billing on a merged patient),
  // silently fall back to personal-details — same as Flutter
  // `selectedTab = 0` when a tab becomes unavailable.
  const requestedTab = (searchParams.get(PATIENT_EDIT_TAB_PARAM) ?? defaultTab) as TabKey;
  const activeTab: TabKey = tabs.find((t) => t.key === requestedTab && t.visible)
    ? requestedTab
    : 'personal-details';

  // An Overview block's link: its tab, and a chart section when it names one (`?inner=`).
  const openTarget = (target: OverviewTarget) => {
    const next = new URLSearchParams(searchParams);
    if (target.tab === defaultTab) next.delete(PATIENT_EDIT_TAB_PARAM);
    else next.set(PATIENT_EDIT_TAB_PARAM, target.tab);
    if (target.tab === 'chart' && target.section) next.set(CHART_INNER_PARAM, target.section);
    else next.delete(CHART_INNER_PARAM);
    next.delete(CHART_TAB_PARAM);
    setSearchParams(next, { replace: false });
  };

  const setActiveTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === defaultTab) {
      next.delete(PATIENT_EDIT_TAB_PARAM);
    } else {
      next.set(PATIENT_EDIT_TAB_PARAM, key);
    }
    // Strip the Chart-scoped inner-tab param when leaving Chart. Without
    // this, navigating away from `?tab=chart&inner=labs` to e.g. Billing
    // leaves `inner=labs` in the URL — and re-entry to Chart later would
    // skip the canonical default (`allergies`) and land on the stale key.
    // Samer PR #152 review 2026-05-15.
    if (key !== 'chart') {
      next.delete(CHART_INNER_PARAM);
      next.delete(CHART_TAB_PARAM);
    }
    setSearchParams(next, { replace: false });
  };

  // Shell consumes save / dirty state from the active tab body.
  const { saveAction, isDirty } = usePatientEditShell();

  // Feed the shell-level nav guard so leaving via the SideNav rail / profile
  // menu (exits Flutter's pushed-route screen never exposed) prompts to
  // discard instead of silently dropping edits — same confirm the in-page
  // Back button shows. Cleared on unmount so the guard never leaks past the
  // edit page. Flutter parity: `doCancel`/`cancelCreatingDialog`
  // (create_patient_screen.dart:277) guards only the Back button because its
  // side menu is hidden; the React shell keeps the rail mounted.
  const setNavBlocking = useNavGuard((s) => s.setBlocking);
  useEffect(() => {
    setNavBlocking(isDirty);
    return () => setNavBlocking(false);
  }, [isDirty, setNavBlocking]);

  // Hard-navigation guard (browser refresh / tab close / address-bar change) —
  // the SPA nav guard above can't see those. Native beforeunload prompt is the
  // only cross-browser option; copy is browser-controlled (can't match the
  // in-app dialog), which is the accepted standard for hard exits.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy assignment required by Chrome/Firefox to trigger the prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const handleCancel = () => {
    if (isDirty) {
      setCancelDialogOpen(true);
    } else {
      navigate(backTo ?? '/patients');
    }
  };
  const confirmCancel = () => {
    setCancelDialogOpen(false);
    navigate(backTo ?? '/patients');
  };

  // Save button visibility: hidden on Billing tab (Flutter quirk —
  // `selectedTab == 4 ? Container() : Button(...)` at :769) and on merged
  // patients (no edits allowed once merged).
  const showSave = activeTab !== 'billing' && activeTab !== 'overview' && activeTab !== 'care-plan' && !isMerged;
  // The form action bar shows only on the tabs that own a form (they register a save action): Personal details and
  // Contact details. Overview, Chart, Care plan, Community, Billing and Appointments have none, so no lone Cancel
  // bar; the guarded exit there is the top bar's "Patients" crumb (nav guard above). Merged records are read-only.
  const showFormBar = showSave && (activeTab === 'personal-details' || activeTab === 'contact-details');

  // Save/Next button (in the form action bar below the content, not the page header). Tab bodies register the handler + disabled
  // state via the shell context. If no tab has registered (e.g. the stub
  // bodies in this PR), render a disabled placeholder so the slot still
  // reflects the design.
  const saveLabel =
    saveAction?.label ??
    (isCreateMode && activeTab === 'personal-details' ? 'Next' : 'Save');
  const saveDisabled = saveAction?.disabled ?? true;
  const saveBusy = saveAction?.busy ?? false;
  const handleSaveClick = saveAction?.onClick;
  // Surface WHY the Save button is disabled (first blocking required field),
  // so the user isn't left guessing — a required-but-empty email or a missing
  // nationality reads as "Save just won't turn on" otherwise. Only shown while
  // genuinely blocked (not merely busy mid-save).
  const saveDisabledReason =
    saveDisabled && !saveBusy ? (saveAction?.disabledReason ?? null) : null;

  // The form action bar's status line (the approved design's FormActionBar `status`), from the flags the tab already
  // registers: busy → Saving…, a failed last save → its error words, saved with nothing left → All changes saved,
  // unsaved edits → Unsaved changes. No line before the first edit.
  const savedClean = saveAction?.saved === true && saveAction.dirty === false;
  const saveStatus: FormSaveStatus | undefined = saveBusy
    ? 'saving'
    : saveAction?.errorText
      ? 'error'
      : savedClean
        ? 'saved'
        : isDirty || saveAction?.dirty === true
          ? 'unsaved'
          : undefined;

  // The header's one line of facts: the person first (age · sex), the MRN once and named, the language, the status and
  // the last update in the design's day-month-year ("Updated 19 Oct 2026", design demo/patient-workspace.ts facts).
  const mrnDigits = patient?.patientMrn?.trim().replace(/^MRN(\s+|$)/i, '');
  const headerFacts = [
    patient?.age?.trim() || null,
    patient?.genderName?.name?.trim() || null,
    // "MRN 1842-9221" (design PaPatientRecordScreen facts: the identifier is named). The stored MRN often
    // already reads "MRN 000-100-0036", so drop that word before naming it, or the header says "MRN MRN".
    mrnDigits ? `MRN ${mrnDigits}` : null,
    patient?.languageName?.name?.trim() ? `Language ${patient.languageName.name.trim()}` : null,
    // The record's status, always (it lived in the chart's facts strip): plain fact text, every tab.
    patient?.isActive === true ? 'Active' : patient?.isActive === false ? 'Inactive' : null,
    patient?.lastUpdated && Number.isFinite(Date.parse(patient.lastUpdated))
      ? `Updated ${formatDateD45(patient.lastUpdated)}`
      : null,
  ].filter((v): v is string => Boolean(v));

  const displayName =
    [patient?.firstName, patient?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(' ') || 'New patient';

  // The one-page chart (always) and, behind the patient pages switch, the Overview and the care plan sit on the white
  // read surface (prototype), not on the grey page behind the forms. The hook is `data-patient-pages` (bridge, `/* patient pages */`).
  const whitePage =
    activeTab === 'chart' || (patientPages && (activeTab === 'overview' || activeTab === 'care-plan'))
      ? { 'data-patient-pages': 'on' }
      : {};

  return (
    <PageScaffold
      variant="detail"
      data-page="patient-record"
      /* Showcase (design log 13 Oct): the patient page uses the one page header — the 48px photo,
         the name as the header title over one line of facts, the tabs on the right, on the
         subline's bottom line. Cancel / Save sit below the content in the form action bar. Back sits before the photo on
         every tab, as main's in-page Back: the same guarded `handleCancel`, back to where the user came from
         (`backTo ?? '/patients'`). The "Patients → Edit Patient" trail is gone (the top bar's breadcrumb names the page). */
      // Showcase: no grey container inside the white frame (the scaffold's detail root is `bg-surface-background`).
      className="bg-surface-card"
      header={
        <PageHeader
          leading={
            <>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                data-slot="page-header-back"
                aria-label="Back to patients list"
                icon={<ChevronLeft aria-hidden />}
                onClick={handleCancel}
              />
              {isLoading && !isCreateMode ? (
                <Skeleton className="h-12 w-12 rounded-full" />
              ) : (
                <Avatar
                  scale="compact"
                  tone="patient"
                  name={isCreateMode ? undefined : displayName}
                  alt={displayName}
                  fallbackIcon={<User className="size-6" aria-hidden />}
                  src={patient?.photoUrl ?? undefined}
                  size={48}
                />
              )}
            </>
          }
          title={isLoading && !isCreateMode ? <Skeleton className="h-5 w-48" /> : displayName}
          // Merged: the soft warning status chip (design PaPatientRecordScreen `badge`, personal-details-merged.html).
          tag={isMerged ? <StatusChip semantic="warning" tone="soft">Merged</StatusChip> : undefined}
          // Staff chat (SR-14): message a colleague about this patient. Only with `?x=staff-chat` and `?x=staff-chat-patient`
          // (patient threads wait for Samer's call). Nothing when off or creating.
          actions={!isCreateMode && isStaffChatPatientOn() ? <MessageColleagueAction patientId={patientId} /> : undefined}
          meta={
            !isCreateMode && headerFacts.length > 0 ? (
              <span data-testid="patient-header-facts">{headerFacts.join(' · ')}</span>
            ) : undefined
          }
          tabs={
            <SubTabs
              tabs={tabs
                .filter((t) => t.visible)
                .map(
                  (t): SubTabDef => ({
                    id: t.key,
                    label: t.label,
                    disabled: !t.enabled,
                    panelId: `patient-edit-panel-${t.key}`,
                  }),
                )}
              value={activeTab}
              onValueChange={(v) => setActiveTab(v as TabKey)}
            />
          }
        />
      }
      bodyProps={{
        // Showcase: content at 48px sides, 32px top, 48px bottom, like the other app pages.
        className: 'px-12 pt-8 pb-12',
        id: `patient-edit-panel-${activeTab}`,
        role: 'tabpanel',
        'aria-labelledby': `sub-tab-${activeTab}`,
        ...whitePage,
      }}
      overlay={
        <CancelChangesDialog
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          onConfirm={confirmCancel}
        />
      }
    >
      {activeTab === 'overview' ? (
        <PatientOverviewTab patient={patient ?? null} onOpen={openTarget} />
      ) : activeTab === 'personal-details' ? (
        <PersonalDetailsTab patient={patient ?? null} isCreateMode={isCreateMode} hideInfoSummary />
      ) : activeTab === 'contact-details' ? (
        <ContactDetailsTab patient={patient ?? null} />
      ) : activeTab === 'chart' ? (
        <ChartTab patient={patient ?? null} />
      ) : activeTab === 'care-plan' ? (
        <PatientCarePlanTab patient={patient ?? null} />
      ) : activeTab === 'community' ? (
        <CommunityTab patient={patient ?? null} />
      ) : activeTab === 'billing' ? (
        <BillingTab patient={patient ?? null} />
      ) : activeTab === 'appointments' ? (
        <AppointmentsTab patient={patient ?? null} />
      ) : null}
      {/* Cancel / Save commit or discard the form's edits, so they sit with the form, one layer below the page
          header (Antonin, 15 Oct). Same handlers and states as before; Cancel is the guarded exit; the form tabs only. */}
      {showFormBar ? (
        <FormActionBar
          status={saveStatus}
          errorText={saveAction?.errorText}
          note={
            saveDisabledReason ? (
              <>
                <Info className="size-4 shrink-0 text-text-secondary" aria-hidden />
                <span className="truncate">{saveDisabledReason}</span>
              </>
            ) : undefined
          }
        >
          <Button type="button" variant="tertiary" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          {/* PA's Save rule kept: once a save went through and nothing is left to save, Save is not drawn; the saved
              status on the left says so (it took over the "Changes saved" line that stood in Save's place). */}
          {savedClean ? null : (
            <Button type="button" onClick={handleSaveClick} disabled={saveDisabled || saveBusy} size="sm">
              {saveBusy ? `${saveLabel}…` : saveLabel}
            </Button>
          )}
        </FormActionBar>
      ) : null}
    </PageScaffold>
  );
}

